import axiosInstance from '@/setup/axios/axiosInstance'
import { getSelectedCompany } from '@/modules/auth/helpers/CompanyStorage';
import { 
  Customer, 
  CustomerListResponse, 
  CreateCustomerRequest, 
  UpdateCustomerRequest,
  CustomerSearchParams,
  CustomerStatistics,
  BulkUpdateRequest
} from '../models/CustomerModel';

// Get all customers
export const getCustomers = async (params?: CustomerSearchParams): Promise<CustomerListResponse | Customer[]> => {
  try {
    const selectedCompany = getSelectedCompany();
    const selectedCompanyId = selectedCompany?.company_id;
    const effectiveParams: CustomerSearchParams = { ...(params || {}) };

    if (
      selectedCompanyId &&
      selectedCompanyId !== 'global' &&
      !effectiveParams.company &&
      !effectiveParams.company_id
    ) {
      effectiveParams.company = selectedCompanyId;
    }

    const response = await axiosInstance.get('/customers/', { params: effectiveParams });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get customer by ID
export const getCustomerById = async (id: string): Promise<Customer> => {
  const response = await axiosInstance.get(`/customers/${id}/`);
  return response.data;
};

// Create new customer
export const createCustomer = async (customerData: CreateCustomerRequest): Promise<Customer> => {
  const response = await axiosInstance.post('/customers/', customerData);
  return response.data;
};

// Update customer
export const updateCustomer = async (id: string, customerData: UpdateCustomerRequest): Promise<Customer> => {
  const response = await axiosInstance.put(`/customers/${id}/`, customerData);
  return response.data;
};

// Delete customer
export const deleteCustomer = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/customers/${id}/`);
};

// Search customers
export const searchCustomers = async (params: CustomerSearchParams): Promise<CustomerListResponse> => {
  const response = await axiosInstance.get('/customers/search/', { params });
  return response.data;
};

// Get customer statistics
export const getCustomerStatistics = async (): Promise<CustomerStatistics> => {
  const response = await axiosInstance.get('/customers/statistics/');
  return response.data;
};

// Bulk update customers
export const bulkUpdateCustomers = async (data: BulkUpdateRequest): Promise<{ message: string; updated_count: number }> => {
  const response = await axiosInstance.post('/customers/bulk_update/', data);
  return response.data;
};

// Add customer to company
export const addCustomerToCompany = async (customerId: string, companyId: string): Promise<{ message: string }> => {
  const response = await axiosInstance.post(`/customers/${customerId}/add_to_company/`, { company_id: companyId });
  return response.data;
};

// Remove customer from company
export const removeCustomerFromCompany = async (customerId: string, companyId: string): Promise<{ message: string }> => {
  const response = await axiosInstance.post(`/customers/${customerId}/remove_from_company/`, { company_id: companyId });
  return response.data;
};

// Soft delete customers
export const softDeleteCustomers = async (customerIds: string[]) => {
  try {
    const response = await axiosInstance.post("/customers/soft_delete/", {
      customer_ids: customerIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Restore deleted customers
export const restoreCustomers = async (customerIds: string[]) => {
  try {
    const response = await axiosInstance.post("/customers/restore_deleted/", {
      customer_ids: customerIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Permanently delete customers
export const permanentDeleteCustomers = async (customerIds: string[]) => {
  try {
    const response = await axiosInstance.post("/customers/permanent_delete/", {
      customer_ids: customerIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get deleted customers
export const getDeletedCustomers = async () => {
  try {
    const response = await axiosInstance.get("/customers/?show_deleted=true");
    return response;
  } catch (error) {
    throw error;
  }
};
